import { Task, Timestamp } from '@griply/types';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info12, MoreHorizontal, Repeat16 } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { Colors, IconButton, Sizes } from 'shared/components/ui/icon-button';
import { Tooltip } from 'shared/components/ui/tooltip';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';

import { DayEntry } from '../upcoming/types';
import { ActionMenu } from './action-menu';
import * as Styled from './upcoming-slot.style';
import { useSlotData } from './use-slot-data';

export type UntilCompletedSlotProps = {
  task: Task;
  date: Timestamp;
  entry: DayEntry;
};

export const UntilCompletedSlot: React.FC<UntilCompletedSlotProps> = ({
  task,
  date,
  entry,
}) => {
  const { t } = useTranslation();

  const infoIconRef = useRef<HTMLSpanElement>(null);
  const actionMenuButtonRef = useRef<HTMLButtonElement>(null);

  const {
    position: contextMenuPosition,
    open: openContextMenu,
    close: closeContextMenu,
  } = useContextMenuState();

  const [menuOpen, setMenuOpen] = useState(false);
  const openMenu = () => setMenuOpen(true);
  const closeMenu = () => {
    setMenuOpen(false);
    closeContextMenu();
  };

  const onContextMenuOpen = (e: React.MouseEvent) => {
    openContextMenu(e);
    setMenuOpen(true);
  };

  const { timeLabel } = useSlotData({ task, date, entry });

  return (
    <Styled.Container as="div" onContextMenu={onContextMenuOpen}>
      <Styled.StartIconContainer>
        <Icon icon={Repeat16} />
      </Styled.StartIconContainer>
      <Styled.LabelContainer $hasEntry>
        <Styled.LabelText>
          {t('habit.schedule.frequency.until-completed')}
        </Styled.LabelText>
        <Styled.InfoIconContainer ref={infoIconRef}>
          <Icon icon={Info12} width="1.2rem" height="1.2rem" />
        </Styled.InfoIconContainer>
        <Tooltip
          label={t('habit.schedule.frequency.until-completed.tooltip')}
          referenceElement={infoIconRef}
        />
      </Styled.LabelContainer>
      <Styled.EndContainer>
        <IconButton
          icon={MoreHorizontal}
          onClick={openMenu}
          color={Colors.Default}
          size={Sizes.Small}
          ref={actionMenuButtonRef}
        />
        {menuOpen && (
          <ActionMenu
            task={task}
            date={date}
            timeLabel={timeLabel}
            onDate={closeMenu}
            onTime={closeMenu}
            referenceElement={
              contextMenuPosition ? undefined : actionMenuButtonRef
            }
            location={contextMenuPosition}
            onOutside={closeMenu}
            entryBased={entry.isUnplannedEntry}
            isGeneratedFrequencyEntry={entry.isGeneratedFrequencyEntry}
            isFrequencyRow
          />
        )}
      </Styled.EndContainer>
    </Styled.Container>
  );
};
